import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 30000,
});

const getAccessToken = () => {
  return localStorage.getItem('accessToken');
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const refreshAccessToken = async () => {
  try {
      const refreshToken = localStorage.getItem('refreshToken'); // Get refresh token

      if (!refreshToken) {
          throw new Error('No refresh token found. Force logout.');
      }

      const response = await api.post('auth/refresh/subUser', {
          refreshToken: refreshToken,
      });

      if (response.data && response.data.accessToken) {
          const newAccessToken = response.data.accessToken;
          const newRefreshToken = response.data.refreshToken || refreshToken; 

          localStorage.setItem('accessToken', newAccessToken);
          localStorage.setItem('refreshToken', newRefreshToken); 
          return newAccessToken;
      } else {
          throw new Error('Refresh successful but no new access token. Force logout.');
      }
  } catch (error) {
      console.error('Error refreshing token:', error);
      localStorage.clear(); 
      throw error; 
  }
};



// Request/Response logging (like HttpLoggingInterceptor)
api.interceptors.request.use(
  (config) => {
    const url = config.url;

    if (
        url &&
        (url.includes('auth/refresh/subUser') ||
            url.includes('sub-user/login'))
    ) {
        return config;
    }

    const token = getAccessToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
      return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
      return response;
  },
  async (error) => {
      const originalRequest = error.config;

      // Check if the error is 401 (Unauthorized) and it's not the refresh token endpoint itself
      if (
          error.response &&
          error.response.status === 401 &&
          originalRequest.url &&
          !originalRequest.url.includes('auth/refresh/subUser') &&
          !originalRequest._retry // Prevent infinite loop for failed retries
      ) {
          // Mark the original request for retry
          originalRequest._retry = true;

          // If a refresh is already in progress, add the request to the queue
          if (isRefreshing) {
              return new Promise((resolve, reject) => {
                  failedQueue.push({ resolve, reject });
              })
                  .then((token) => {
                      originalRequest.headers.Authorization = `Bearer ${token}`;
                      return api(originalRequest); 
                  })
                  .catch((err) => {
                      return Promise.reject(err);
                  });
          }

          isRefreshing = true; // Set flag that refresh is in progress

          try {
              const newAccessToken = await refreshAccessToken(); // Attempt to refresh the token
              isRefreshing = false;
              processQueue(null, newAccessToken); // Process the queue with the new token
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              return api(originalRequest); // Retry the original request
          } catch (refreshError) {
              isRefreshing = false;
              processQueue(refreshError); // Process the queue with the error (force logout)
              localStorage.clear(); // Example: clear tokens
              return Promise.reject(refreshError); // Reject the original request
          }
      }

      if (
          error.response &&
          error.response.status === 401 &&
          originalRequest.url &&
          originalRequest.url.includes('auth/refresh/subUser')
      ) {
          console.error('Self-refreshing token API got 401. Forcing logout.');
          localStorage.clear();
      }

      return Promise.reject(error);
  }
);

export const forceLogout = () => {
  console.log('Triggering force logout from React application.');
  localStorage.clear();
  window.location.href = '/login'; 
};

export default api;
